'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import {
  createRejectFormSchema,
  REJECT_TARGET_STAGES,
  type RejectFormValues
} from '../schemas';
import { configurationQueries } from '@/features/configurations';
import type { RejectionReason } from '@/features/configurations';
import { useRejectStageMutation } from '../services/review.mutations';

interface ReviewRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registrationId: number;
  onSuccess?: () => void;
}

export function ReviewRejectDialog({
  open,
  onOpenChange,
  registrationId,
  onSuccess
}: ReviewRejectDialogProps) {
  const t = useTranslations('review');
  const tv = useTranslations('validation');
  const locale = useLocale() as 'en' | 'ar';
  const rejectMutation = useRejectStageMutation();
  const { data: configData } = useQuery(configurationQueries.all());
  const configSettings = (configData as any)?.data ?? configData ?? {};
  const perStageReasons: Record<string, RejectionReason[]> =
    configSettings.rejection_reasons &&
    !Array.isArray(configSettings.rejection_reasons)
      ? (configSettings.rejection_reasons as Record<string, RejectionReason[]>)
      : {};

  // Workflow: registration → sticking → review
  const targetStages = REJECT_TARGET_STAGES;

  const defaultStage = 'sticking';

  // Use a ref so the resolver closure always reads the latest schema
  const schemaRef = useRef(createRejectFormSchema(tv, false));

  const form = useForm<RejectFormValues>({
    resolver: (...args) => zodResolver(schemaRef.current)(...args),
    defaultValues: {
      target_stage: defaultStage,
      predefined_reason: undefined,
      additional_notes: ''
    }
  });

  const selectedStage = form.watch('target_stage');
  const selectedReasonValue = form.watch('predefined_reason');

  const rawReasons: RejectionReason[] = perStageReasons[selectedStage] ?? [];
  const dynamicReasons = useMemo(
    () =>
      rawReasons
        .filter((r) => (r.status ?? 'active') !== 'inactive')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStage, configSettings.rejection_reasons]
  );

  const selectedReason = dynamicReasons.find(
    (r) => r.en === selectedReasonValue
  );
  const isOtherSelected = selectedReason?.en?.toLowerCase() === 'other';

  // Keep schemaRef up to date and re-trigger validation when "Other" changes
  useEffect(() => {
    schemaRef.current = createRejectFormSchema(tv, isOtherSelected);
    if (
      form.formState.isSubmitted ||
      form.formState.touchedFields.additional_notes
    ) {
      form.trigger('additional_notes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOtherSelected]);

  const stageLabels: Record<string, string> = {
    registration: t('stageRegistration'),
    sticking: t('stageSticking')
  };

  const onSubmit = async (values: RejectFormValues) => {
    const matchedReason = dynamicReasons.find(
      (r) => r.en === values.predefined_reason
    );
    const reasonText = matchedReason
      ? (matchedReason[locale] ?? matchedReason.en)
      : values.predefined_reason;
    const rejectionReason = values.additional_notes
      ? `${reasonText}: ${values.additional_notes}`
      : reasonText;

    await rejectMutation.mutateAsync({
      registrationId,
      data: {
        target_stage: values.target_stage,
        rejection_reason: rejectionReason
      }
    });

    form.reset();
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('rejectTitle')}</DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-5'
        >
          <FormField
            control={form.control}
            name='target_stage'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('targetStage')}</FormLabel>
                <Select
                  onValueChange={(val) => {
                    field.onChange(val);
                    form.setValue('predefined_reason', '');
                  }}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {targetStages.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {stageLabels[stage]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='predefined_reason'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('rejectionReason')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className='space-y-2'
                  >
                    {dynamicReasons.map((reason) => (
                      <div
                        key={reason.en}
                        className='flex items-center space-x-2'
                      >
                        <RadioGroupItem value={reason.en} id={reason.en} />
                        <Label htmlFor={reason.en} className='cursor-pointer'>
                          {reason[locale] ?? reason.en}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='additional_notes'
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('additionalNotes')}
                  {isOtherSelected && (
                    <span className='text-destructive ms-0.5'>*</span>
                  )}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t('additionalNotesPlaceholder')}
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              type='submit'
              variant='destructive'
              disabled={rejectMutation.isPending}
            >
              {t('confirmReject')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
